import { useQueryClient } from '@tanstack/react-query'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { useNavigate } from 'react-router'

import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/elements/input-otp'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/elements/tabs'
import { ROUTES } from '@/components/PrivateRoute/PrivateRoute'
import { cleanPhone, formatPhone, usePhoneMask } from '@/hooks/PhoneNumberMask'
import {
  addUserEmail,
  confirmPhoneChange,
  getUser,
  requestPhoneChange,
  resendUserEmailVerification,
  verifyUserEmail,
} from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const CODE_LENGTH = 6

/**
 * Six-digit code entry shared by both recovery methods.
 */
const CodeStep = ({ destination, onSubmit, onResend, onBack, submitting, resending }) => {
  const form = useForm({ defaultValues: { code: '' } })

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit((data) => onSubmit(data.code))}
      >
        <p className="text-muted-foreground text-sm">
          Enter the 6-digit code we sent to <span className="font-medium">{destination}</span>.
        </p>

        <FormField
          name="code"
          control={form.control}
          rules={{ required: 'Enter the code' }}
          disabled={submitting}
          render={({ field }) => (
            <FormItem className="justify-center">
              <FormControl>
                <InputOTP maxLength={CODE_LENGTH} pattern={REGEXP_ONLY_DIGITS} {...field}>
                  <InputOTPGroup>
                    {[...Array(CODE_LENGTH)].map((_, index) => (
                      <InputOTPSlot
                        // eslint-disable-next-line react/no-array-index-key
                        key={`slot-${index}`}
                        index={index}
                        autoFocus={index === 0}
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-row justify-between gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            Use a different one
          </Button>
          <Button type="button" variant="link" size="sm" disabled={resending} onClick={onResend}>
            Resend Code
          </Button>
        </div>

        <Button type="submit" loading={submitting} className="w-full">
          Verify
        </Button>
      </form>
    </Form>
  )
}

CodeStep.propTypes = {
  destination: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  resending: PropTypes.bool,
}

/**
 * Required second way back into an account (RSCX-377).
 *
 * A user's primary address is usually employer-owned, so an account reachable
 * only through it is lost along with the job — and the login code goes to the
 * address they can no longer open. Everyone gets one chance to add a backup
 * before continuing into the app; the gate itself lives in PrivateRoute.
 */
const AddRecoveryMethod = observer(() => {
  const { user, setUser } = useContext(UserStoreContext)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // The pending UserEmail while its code is outstanding; null until one is sent.
  const [pendingEmail, setPendingEmail] = useState(null)
  const [pendingPhone, setPendingPhone] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)

  const handleError = (message) => toast(message, 'error')
  const handleSuccess = (message) => toast(message, 'success')

  const emailForm = useForm({ defaultValues: { email: '' } })
  const phoneForm = useForm({ defaultValues: { phoneNumber: '' } })

  // Watched at the component level rather than inside the field's render prop:
  // a hook called from there only runs while the Phone tab is mounted, which is
  // exactly the conditional-hook shape the rules-of-hooks lint is guarding.
  const { masked: maskedPhone } = usePhoneMask(phoneForm.watch('phoneNumber') ?? '')

  /**
   * Re-reads the user so `recoveryMethodRequired` clears, then leaves the gate.
   * Done before navigating: PrivateRoute would otherwise bounce us straight back.
   */
  const finish = async () => {
    try {
      const refreshed = await getUser(user.id)
      setUser(refreshed)
      queryClient.invalidateQueries({ queryKey: ['users'] })
    } catch (err) {
      handleError(err.message)
      return
    }
    navigate(ROUTES.ACCOUNTS, { replace: true })
  }

  const sendEmailCode = async ({ email }) => {
    setSubmitting(true)
    try {
      const created = await addUserEmail({ userId: user.id, email })
      setPendingEmail(created)
      handleSuccess(`Verification code sent to ${created.email}`)
    } catch (err) {
      emailForm.setError('email', { type: 'manual', message: err.message })
    } finally {
      setSubmitting(false)
    }
  }

  const verifyEmailCode = async (code) => {
    setSubmitting(true)
    try {
      await verifyUserEmail({ userId: user.id, emailId: pendingEmail.id, code })
      handleSuccess(`${pendingEmail.email} verified`)
      await finish()
    } catch (err) {
      handleError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const resendEmailCode = async () => {
    setResending(true)
    try {
      await resendUserEmailVerification({ userId: user.id, emailId: pendingEmail.id })
      handleSuccess('Verification code sent')
    } catch (err) {
      handleError(err.message)
    } finally {
      setResending(false)
    }
  }

  const sendPhoneCode = async ({ phoneNumber }) => {
    const sent = await requestPhoneChange(phoneNumber, handleError, setSubmitting, handleSuccess)
    if (sent) setPendingPhone(phoneNumber)
  }

  const verifyPhoneCode = async (code) => {
    const confirmed = await confirmPhoneChange(code, handleError, setSubmitting, handleSuccess)
    if (confirmed) await finish()
  }

  const resendPhoneCode = async () => {
    await requestPhoneChange(pendingPhone, handleError, setResending, handleSuccess)
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-lg">
        <Card className="overflow-hidden p-0">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <h1>Secure your account</h1>
                <p className="text-muted-foreground mt-2 text-sm text-balance">
                  Right now <span className="font-medium">{user.email}</span> is the only way into
                  your account. If you lose access to it, you can&apos;t receive a login code and
                  can&apos;t log in. Add one backup you keep control of.
                </p>
              </div>

              <Tabs defaultValue="email">
                <TabsList className="w-full">
                  <TabsTrigger value="email" className="flex-1">
                    Backup Email
                  </TabsTrigger>
                  <TabsTrigger value="phone" className="flex-1">
                    Phone Number
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="email" className="mt-6">
                  {pendingEmail ? (
                    <CodeStep
                      destination={pendingEmail.email}
                      submitting={submitting}
                      resending={resending}
                      onSubmit={verifyEmailCode}
                      onResend={resendEmailCode}
                      onBack={() => setPendingEmail(null)}
                    />
                  ) : (
                    <Form {...emailForm}>
                      <form
                        className="flex flex-col gap-6"
                        onSubmit={emailForm.handleSubmit(sendEmailCode)}
                      >
                        <FormField
                          control={emailForm.control}
                          name="email"
                          disabled={submitting}
                          rules={{
                            required: 'Enter an email address',
                            pattern: {
                              value: /.+@.+\..+/,
                              message: 'Enter a valid email address',
                            },
                            validate: (value) =>
                              value.trim().toLowerCase() !== user.email?.toLowerCase() ||
                              'Use an address other than your primary one',
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Personal email address</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="you@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" loading={submitting} className="w-full">
                          Send Code
                        </Button>
                      </form>
                    </Form>
                  )}
                </TabsContent>

                <TabsContent value="phone" className="mt-6">
                  {pendingPhone ? (
                    <CodeStep
                      destination={formatPhone(pendingPhone)}
                      submitting={submitting}
                      resending={resending}
                      onSubmit={verifyPhoneCode}
                      onResend={resendPhoneCode}
                      onBack={() => setPendingPhone(null)}
                    />
                  ) : (
                    <Form {...phoneForm}>
                      <form
                        className="flex flex-col gap-6"
                        onSubmit={phoneForm.handleSubmit(sendPhoneCode)}
                      >
                        <FormField
                          control={phoneForm.control}
                          name="phoneNumber"
                          disabled={submitting}
                          rules={{
                            required: 'Enter a phone number',
                            validate: (value) =>
                              isPossiblePhoneNumber(value, 'US') || 'Enter a valid phone number',
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Mobile phone number</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={maskedPhone}
                                  onChange={(e) => field.onChange(cleanPhone(e.target.value))}
                                  type="tel"
                                  maxLength={14}
                                  inputMode="tel"
                                  autoComplete="tel"
                                  placeholder="(555) 555-1234"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <p className="text-muted-foreground text-sm">
                          By providing a phone number, you agree to receive SMS messages. Message
                          and data rates may apply.
                        </p>
                        <Button type="submit" loading={submitting} className="w-full">
                          Send Code
                        </Button>
                      </form>
                    </Form>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
})

export default AddRecoveryMethod
